import { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "@/App";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from "@/components/ui/dialog";
import {
  Shield, Users, Trophy, Settings, Loader2, Trash2, Ban, CheckCircle,
  Eye, Search, UserX, ArrowLeft
} from "lucide-react";
import { UserDetailView } from "@/components/admin/UserDetailView";

export default function AdminPanel() {
  const { isSuperAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [filterUserTournaments, setFilterUserTournaments] = useState("all");
  const [searchTournaments, setSearchTournaments] = useState("");

  const [price, setPrice] = useState(99);
  const [price1m, setPrice1m] = useState(12);
  const [price3m, setPrice3m] = useState(30);
  const [price6m, setPrice6m] = useState(55);
  const [price9m, setPrice9m] = useState(75);
  const [currency, setCurrency] = useState("eur");
  const [trialDays, setTrialDays] = useState(30);
  const [trialCats, setTrialCats] = useState(4);
  const [trialMaxPlayers, setTrialMaxPlayers] = useState(32);
  const [reminderDay1, setReminderDay1] = useState(30);
  const [reminderDay2, setReminderDay2] = useState(7);
  const [reminderDay3, setReminderDay3] = useState(1);
  const [filterStatus, setFilterStatus] = useState("all");

  const fetchAll = useCallback(async () => {
    if (!isSuperAdmin) return;
    try {
      const [u, t, s] = await Promise.all([
        api.get("/admin/users"),
        api.get("/admin/tournaments"),
        api.get("/admin/settings"),
      ]);
      setUsers(u);
      setTournaments(t);
      setSettings(s);
      setPrice(s.subscription_price || 99);
      setPrice1m(s.price_1_month || 12);
      setPrice3m(s.price_3_months || 30);
      setPrice6m(s.price_6_months || 55);
      setPrice9m(s.price_9_months || 75);
      setCurrency(s.subscription_currency || "eur");
      setTrialDays(s.trial_days || 30);
      setTrialCats(s.trial_max_categories || 4);
      setTrialMaxPlayers(s.trial_max_players_per_category || 32);
      const rd = s.subscription_reminder_days || [30, 7, 1];
      setReminderDay1(rd[0] ?? 30);
      setReminderDay2(rd[1] ?? 7);
      setReminderDay3(rd[2] ?? 1);
    } catch (e) {
      toast.error("Errore nel caricamento dati admin");
    } finally {
      setLoading(false);
    }
  }, [isSuperAdmin]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filteredUsers = useMemo(() => {
    let result = users;
    if (filterStatus !== "all") {
      result = result.filter(u => {
        const status = u.subscription?.status;
        if (filterStatus === "active") return status === "active";
        if (filterStatus === "trial") return status === "trial";
        if (filterStatus === "expired") return !status || status === "expired" || status === "none";
        return true;
      });
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(u => (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q));
    }
    return result;
  }, [users, searchQuery, filterStatus]);

  const stats = useMemo(() => ({
    total: users.length,
    active: users.filter(u => u.subscription?.status === "active").length,
    trial: users.filter(u => u.subscription?.status === "trial").length,
    blocked: users.filter(u => u.blocked).length,
  }), [users]);

  if (!isSuperAdmin) {
    return <div className="text-center py-20 text-slate-500">Accesso riservato al super-admin</div>;
  }
  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-green-700" /></div>;
  }

  const saveSettings = async () => {
    try {
      await api.put("/admin/settings", {
        subscription_price: parseFloat(price),
        subscription_currency: currency,
        price_1_month: parseFloat(price1m),
        price_3_months: parseFloat(price3m),
        price_6_months: parseFloat(price6m),
        price_9_months: parseFloat(price9m),
        trial_days: parseInt(trialDays),
        trial_max_categories: parseInt(trialCats),
        trial_max_players_per_category: parseInt(trialMaxPlayers),
        subscription_reminder_days: [
          parseInt(reminderDay1) || 30,
          parseInt(reminderDay2) || 7,
          parseInt(reminderDay3) || 1,
        ].filter(d => d > 0).sort((a, b) => b - a),
      });
      toast.success("Impostazioni salvate");
    } catch (e) {
      toast.error("Errore nel salvataggio");
    }
  };

  const changeRole = async (uid, role) => {
    try {
      await api.put(`/admin/users/${uid}/role?role=${role}`);
      toast.success("Ruolo aggiornato");
      setUsers(users.map(u => u.user_id === uid ? { ...u, role } : u));
    } catch (e) {
      toast.error("Errore");
    }
  };

  const changeSubscription = async (uid, status) => {
    try {
      await api.put(`/admin/users/${uid}/subscription?status=${status}`);
      toast.success("Abbonamento aggiornato");
      const refreshed = await api.get("/admin/users");
      setUsers(refreshed);
    } catch (e) {
      toast.error("Errore");
    }
  };

  const toggleBlock = async (uid, block) => {
    try {
      await api.put(`/admin/users/${uid}/block?blocked=${block}`);
      toast.success(block ? "Utente bloccato" : "Utente sbloccato");
      setUsers(users.map(u => u.user_id === uid ? { ...u, blocked: block } : u));
    } catch (e) {
      toast.error(e?.message || "Errore");
    }
    setConfirmDialog(null);
  };

  const deleteUser = async (uid) => {
    try {
      await api.del(`/admin/users/${uid}`);
      toast.success("Utente eliminato");
      setUsers(users.filter(u => u.user_id !== uid));
      if (selectedUser === uid) { setSelectedUser(null); setUserDetail(null); }
    } catch (e) {
      toast.error(e?.message || "Errore");
    }
    setConfirmDialog(null);
  };

  const deleteTournament = async (tid) => {
    try {
      await api.del(`/admin/tournaments/${tid}`);
      toast.success("Torneo eliminato");
      setTournaments(tournaments.filter(t => t.tournament_id !== tid));
    } catch (e) {
      toast.error("Errore");
    }
    setConfirmDialog(null);
  };

  const viewUserDetail = async (uid) => {
    setSelectedUser(uid);
    setDetailLoading(true);
    try {
      const detail = await api.get(`/admin/users/${uid}`);
      setUserDetail(detail);
    } catch (e) {
      toast.error("Errore caricamento dettagli");
    } finally {
      setDetailLoading(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return "-";
    try { return new Date(d).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" }); } catch (e) { console.error("Date format error:", e); return d; }
  };

  return (
    <div className="space-y-6" data-testid="admin-panel">
      <div>
        <p className="text-purple-700 text-xs font-bold uppercase tracking-widest mb-1">Super Admin</p>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold uppercase tracking-tight text-slate-900">Pannello Amministrazione</h1>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="bg-white"><CardContent className="pt-4 pb-3 px-4">
          <p className="text-xs font-bold uppercase tracking-wide text-slate-400">Utenti Totali</p>
          <p className="text-2xl font-bold text-slate-900 mt-1">{stats.total}</p>
        </CardContent></Card>
        <Card className="bg-white"><CardContent className="pt-4 pb-3 px-4">
          <p className="text-xs font-bold uppercase tracking-wide text-green-600">Abbonati Attivi</p>
          <p className="text-2xl font-bold text-green-700 mt-1">{stats.active}</p>
        </CardContent></Card>
        <Card className="bg-white"><CardContent className="pt-4 pb-3 px-4">
          <p className="text-xs font-bold uppercase tracking-wide text-amber-600">In Prova</p>
          <p className="text-2xl font-bold text-amber-700 mt-1">{stats.trial}</p>
        </CardContent></Card>
        <Card className="bg-white"><CardContent className="pt-4 pb-3 px-4">
          <p className="text-xs font-bold uppercase tracking-wide text-red-600">Bloccati</p>
          <p className="text-2xl font-bold text-red-700 mt-1">{stats.blocked}</p>
        </CardContent></Card>
      </div>

      <Tabs defaultValue="users">
        <TabsList className="bg-white border border-slate-200 p-1 rounded-sm">
          <TabsTrigger value="users" data-testid="admin-tab-users" className="rounded-sm gap-1 data-[state=active]:bg-purple-700 data-[state=active]:text-white">
            <Users className="h-4 w-4" /> Utenti ({users.length})
          </TabsTrigger>
          <TabsTrigger value="tournaments" data-testid="admin-tab-tournaments" className="rounded-sm gap-1 data-[state=active]:bg-purple-700 data-[state=active]:text-white">
            <Trophy className="h-4 w-4" /> Tornei ({tournaments.length})
          </TabsTrigger>
          <TabsTrigger value="settings" data-testid="admin-tab-settings" className="rounded-sm gap-1 data-[state=active]:bg-purple-700 data-[state=active]:text-white">
            <Settings className="h-4 w-4" /> Impostazioni
          </TabsTrigger>
        </TabsList>

        {/* USERS TAB */}
        <TabsContent value="users" className="mt-6">
          {selectedUser && userDetail ? (
            <UserDetailView
              user={userDetail}
              loading={detailLoading}
              onBack={() => { setSelectedUser(null); setUserDetail(null); }}
              onBlock={(uid, b) => setConfirmDialog({ type: b ? "block" : "unblock", uid, name: userDetail.name })}
              onDelete={(uid) => setConfirmDialog({ type: "deleteUser", uid, name: userDetail.name })}
              onChangeRole={changeRole}
              onChangeSubscription={changeSubscription}
              formatDate={formatDate}
            />
          ) : (
            <Card className="bg-white">
              <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-3">
                <CardTitle className="font-heading text-lg font-bold uppercase">
                  Gestione Utenti
                  {filterStatus !== "all" && <span className="ml-2 text-sm text-purple-600 font-normal normal-case">({filteredUsers.length})</span>}
                </CardTitle>
                <div className="flex items-center gap-2 flex-wrap">
                  {/* Status filter buttons */}
                  <div className="flex gap-1 bg-slate-100 rounded-sm p-1">
                    {[
                      { key: "all", label: "Tutti", count: users.length },
                      { key: "active", label: "Attivi", count: stats.active },
                      { key: "trial", label: "In Prova", count: stats.trial },
                      { key: "expired", label: "Scaduti", count: users.length - stats.active - stats.trial },
                    ].map(f => (
                      <button
                        key={f.key}
                        data-testid={`admin-filter-${f.key}`}
                        onClick={() => setFilterStatus(f.key)}
                        className={`px-3 py-1 text-xs font-bold uppercase rounded-sm transition-all ${
                          filterStatus === f.key
                            ? "bg-purple-700 text-white shadow-sm"
                            : "text-slate-500 hover:text-slate-700"
                        }`}
                      >
                        {f.label} <span className="opacity-70">({f.count})</span>
                      </button>
                    ))}
                  </div>
                  <div className="relative w-56">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                    <Input
                      data-testid="admin-user-search"
                      placeholder="Cerca per nome o email..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9 rounded-sm h-9 text-sm"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="max-h-[500px] overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs uppercase">Nome</TableHead>
                        <TableHead className="text-xs uppercase">Email</TableHead>
                        <TableHead className="text-xs uppercase">Ruolo</TableHead>
                        <TableHead className="text-xs uppercase">Stato</TableHead>
                        <TableHead className="text-xs uppercase">Abbonamento</TableHead>
                        <TableHead className="text-xs uppercase">Scadenza</TableHead>
                        <TableHead className="text-xs uppercase">Tornei</TableHead>
                        <TableHead className="text-xs uppercase">Ultimo Accesso</TableHead>
                        <TableHead className="text-xs uppercase">Azioni</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredUsers.map(u => {
                        const sub = u.subscription || {};
                        const isBlocked = !!u.blocked;
                        return (
                          <TableRow key={u.user_id} data-testid={`admin-user-${u.user_id}`} className={isBlocked ? "opacity-50" : ""}>
                            <TableCell className="font-medium">{u.name || "-"}</TableCell>
                            <TableCell className="text-sm text-slate-500">{u.email}</TableCell>
                            <TableCell>
                              <Badge className={`rounded-sm text-xs ${u.role === "superadmin" ? "bg-purple-100 text-purple-800 hover:bg-purple-100" : "bg-slate-100 text-slate-700 hover:bg-slate-100"}`}>
                                {u.role === "superadmin" ? "Super Admin" : "Utente"}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {isBlocked ? (
                                <Badge className="rounded-sm text-xs bg-red-100 text-red-800 hover:bg-red-100">Bloccato</Badge>
                              ) : (
                                <Badge className="rounded-sm text-xs bg-green-100 text-green-800 hover:bg-green-100">Attivo</Badge>
                              )}
                            </TableCell>
                            <TableCell>
                              <Badge className={`rounded-sm text-xs ${
                                sub.status === "active" ? "bg-green-100 text-green-800 hover:bg-green-100" :
                                sub.status === "trial" ? "bg-amber-100 text-amber-800 hover:bg-amber-100" :
                                "bg-red-100 text-red-800 hover:bg-red-100"
                              }`}>
                                {sub.status === "active" ? "Attivo" : sub.status === "trial" ? "Prova" : "Scaduto"}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-xs text-slate-500">{formatDate(sub.subscription_end || sub.trial_end)}</TableCell>
                            <TableCell className="text-sm font-medium">{u.tournaments_count || 0}</TableCell>
                            <TableCell className="text-xs text-slate-500">{formatDate(u.last_login)}</TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                <Button data-testid={`admin-view-user-${u.user_id}`} variant="outline" size="sm" onClick={() => viewUserDetail(u.user_id)} className="h-7 rounded-sm gap-1 text-xs text-purple-700 border-purple-200 hover:bg-purple-50 px-2">
                                  <Eye className="h-3.5 w-3.5" /> Dettagli
                                </Button>
                                {u.role !== "superadmin" && (
                                  <>
                                    <Button data-testid={`admin-block-user-${u.user_id}`} variant="ghost" size="sm" onClick={() => setConfirmDialog({ type: isBlocked ? "unblock" : "block", uid: u.user_id, name: u.name })} className={`h-7 w-7 p-0 ${isBlocked ? "text-green-600 hover:text-green-800" : "text-amber-500 hover:text-amber-700"}`} title={isBlocked ? "Sblocca" : "Blocca"}>
                                      {isBlocked ? <CheckCircle className="h-3.5 w-3.5" /> : <Ban className="h-3.5 w-3.5" />}
                                    </Button>
                                    <Button data-testid={`admin-delete-user-${u.user_id}`} variant="ghost" size="sm" onClick={() => setConfirmDialog({ type: "deleteUser", uid: u.user_id, name: u.name })} className="h-7 w-7 p-0 text-red-500 hover:text-red-700" title="Elimina">
                                      <Trash2 className="h-3.5 w-3.5" />
                                    </Button>
                                  </>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {filteredUsers.length === 0 && (
                        <TableRow><TableCell colSpan={9} className="text-center py-8 text-slate-400">Nessun utente trovato</TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* TOURNAMENTS TAB */}
        <TabsContent value="tournaments" className="mt-6">
          <Card className="bg-white">
            <CardHeader className="flex flex-row items-center justify-between gap-3 flex-wrap">
              <CardTitle className="font-heading text-lg font-bold uppercase">Tutti i Tornei</CardTitle>
              <div className="flex items-center gap-2">
                <div className="relative w-56">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                  <Input
                    data-testid="admin-tournament-search"
                    placeholder="Cerca..."
                    value={searchTournaments}
                    onChange={(e) => setSearchTournaments(e.target.value)}
                    className="pl-9 rounded-sm h-9 text-sm"
                  />
                </div>
                <Select value={filterUserTournaments} onValueChange={setFilterUserTournaments}>
                  <SelectTrigger data-testid="admin-tournament-user-filter" className="w-[200px] h-9 rounded-sm text-sm">
                    <SelectValue placeholder="Filtra per utente" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti gli utenti</SelectItem>
                    {users.filter(u => u.tournaments_count > 0).map(u => (
                      <SelectItem key={u.user_id} value={u.user_id}>{u.name || u.email}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {(() => {
                let filtered = filterUserTournaments === "all" ? tournaments : tournaments.filter(t => t.organizer_id === filterUserTournaments);
                if (searchTournaments) {
                  const q = searchTournaments.toLowerCase();
                  filtered = filtered.filter(t =>
                    (t.name || "").toLowerCase().includes(q) ||
                    (t.organizer_name || "").toLowerCase().includes(q) ||
                    (t.start_date || "").includes(q) ||
                    (t.end_date || "").includes(q) ||
                    (t.categories || []).some(c => (c.name || "").toLowerCase().includes(q))
                  );
                }
                return (
                  <>
                    <Badge variant="outline" className="rounded-sm mb-3">{filtered.length} tornei</Badge>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-xs uppercase">Nome</TableHead>
                          <TableHead className="text-xs uppercase">Organizzatore</TableHead>
                          <TableHead className="text-xs uppercase">Date</TableHead>
                          <TableHead className="text-xs uppercase">Categorie</TableHead>
                          <TableHead className="text-xs uppercase">Giocatori</TableHead>
                          <TableHead className="text-xs uppercase">Azioni</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filtered.map(t => (
                          <TableRow key={t.tournament_id}>
                            <TableCell className="font-medium">{t.name}</TableCell>
                            <TableCell className="text-sm text-slate-500">{t.organizer_name}</TableCell>
                            <TableCell className="text-xs">{t.start_date} - {t.end_date}</TableCell>
                            <TableCell><Badge variant="outline" className="rounded-sm text-xs">{t.categories?.length || 0}</Badge></TableCell>
                            <TableCell><Badge variant="outline" className="rounded-sm text-xs">{t.total_players || 0}</Badge></TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                <Link to={`/tournament/${t.tournament_id}`}>
                                  <Button data-testid={`admin-view-tournament-${t.tournament_id}`} variant="outline" size="sm" className="h-7 rounded-sm gap-1 text-xs text-purple-700 border-purple-200 hover:bg-purple-50 px-2">
                                    <Eye className="h-3.5 w-3.5" /> Dettagli
                                  </Button>
                                </Link>
                                <Button data-testid={`admin-delete-tournament-${t.tournament_id}`} variant="ghost" size="sm" onClick={() => setConfirmDialog({ type: "deleteTournament", tid: t.tournament_id, name: t.name })} className="text-red-500 hover:text-red-700 h-7 w-7 p-0">
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                        {filtered.length === 0 && (
                          <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">Nessun torneo trovato</TableCell></TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </>
                );
              })()}
            </CardContent>
          </Card>
        </TabsContent>

        {/* SETTINGS TAB */}
        <TabsContent value="settings" className="mt-6">
          <Card className="bg-white max-w-2xl">
            <CardHeader><CardTitle className="font-heading text-lg font-bold uppercase">Impostazioni Piattaforma</CardTitle></CardHeader>
            <CardContent className="space-y-6">
              {/* Currency */}
              <div>
                <Label className="text-xs font-bold uppercase tracking-wide">Valuta</Label>
                <Select value={currency} onValueChange={setCurrency}>
                  <SelectTrigger className="w-24 rounded-sm mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="eur">EUR</SelectItem>
                    <SelectItem value="usd">USD</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Subscription Prices */}
              <div>
                <Label className="text-xs font-bold uppercase tracking-wide mb-3 block">Prezzi Abbonamento</Label>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  <div className="border border-slate-200 rounded-sm p-3">
                    <p className="text-xs text-slate-500 font-medium mb-1">1 Mese</p>
                    <Input data-testid="admin-price-1m" type="number" step="0.01" value={price1m} onChange={(e) => setPrice1m(e.target.value)} className="rounded-sm h-8 text-sm" />
                  </div>
                  <div className="border border-slate-200 rounded-sm p-3">
                    <p className="text-xs text-slate-500 font-medium mb-1">3 Mesi</p>
                    <Input data-testid="admin-price-3m" type="number" step="0.01" value={price3m} onChange={(e) => setPrice3m(e.target.value)} className="rounded-sm h-8 text-sm" />
                  </div>
                  <div className="border border-slate-200 rounded-sm p-3">
                    <p className="text-xs text-slate-500 font-medium mb-1">6 Mesi</p>
                    <Input data-testid="admin-price-6m" type="number" step="0.01" value={price6m} onChange={(e) => setPrice6m(e.target.value)} className="rounded-sm h-8 text-sm" />
                  </div>
                  <div className="border border-slate-200 rounded-sm p-3">
                    <p className="text-xs text-slate-500 font-medium mb-1">9 Mesi</p>
                    <Input data-testid="admin-price-9m" type="number" step="0.01" value={price9m} onChange={(e) => setPrice9m(e.target.value)} className="rounded-sm h-8 text-sm" />
                  </div>
                  <div className="border border-purple-200 bg-purple-50 rounded-sm p-3">
                    <p className="text-xs text-purple-700 font-bold mb-1">12 Mesi</p>
                    <Input data-testid="admin-price-input" type="number" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} className="rounded-sm h-8 text-sm" />
                  </div>
                </div>
              </div>

              {/* Trial Settings */}
              <div className="flex gap-6 flex-wrap">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wide">Giorni Periodo di Prova</Label>
                  <Input data-testid="admin-trial-days" type="number" value={trialDays} onChange={(e) => setTrialDays(e.target.value)} className="mt-1 rounded-sm w-32" />
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wide">Max Categorie (Prova)</Label>
                  <Input data-testid="admin-trial-cats" type="number" value={trialCats} onChange={(e) => setTrialCats(e.target.value)} className="mt-1 rounded-sm w-32" />
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wide">Max Giocatori/Categoria (Prova)</Label>
                  <Input data-testid="admin-trial-max-players" type="number" value={trialMaxPlayers} onChange={(e) => setTrialMaxPlayers(e.target.value)} className="mt-1 rounded-sm w-32" />
                </div>
              </div>

              {/* Subscription Expiry Reminder Days */}
              <div>
                <Label className="text-xs font-bold uppercase tracking-wide mb-1 block">Promemoria Scadenza Abbonamento</Label>
                <p className="text-xs text-slate-400 mb-3">
                  Inserisci i giorni prima della scadenza in cui inviare un'email di promemoria agli utenti (attivi e in prova).
                  Le email vengono inviate automaticamente ogni 30 minuti.
                </p>
                <div className="flex gap-4 flex-wrap">
                  <div className="border border-amber-200 bg-amber-50 rounded-sm p-3">
                    <p className="text-xs text-amber-700 font-bold mb-1">1° Reminder (giorni prima)</p>
                    <Input data-testid="admin-reminder-day-1" type="number" min="1" max="365" value={reminderDay1} onChange={(e) => setReminderDay1(e.target.value)} className="rounded-sm h-8 text-sm w-24" />
                  </div>
                  <div className="border border-amber-200 bg-amber-50 rounded-sm p-3">
                    <p className="text-xs text-amber-700 font-bold mb-1">2° Reminder (giorni prima)</p>
                    <Input data-testid="admin-reminder-day-2" type="number" min="1" max="365" value={reminderDay2} onChange={(e) => setReminderDay2(e.target.value)} className="rounded-sm h-8 text-sm w-24" />
                  </div>
                  <div className="border border-amber-200 bg-amber-50 rounded-sm p-3">
                    <p className="text-xs text-amber-700 font-bold mb-1">3° Reminder (giorni prima)</p>
                    <Input data-testid="admin-reminder-day-3" type="number" min="1" max="365" value={reminderDay3} onChange={(e) => setReminderDay3(e.target.value)} className="rounded-sm h-8 text-sm w-24" />
                  </div>
                </div>
              </div>

              <Button data-testid="admin-save-settings" onClick={saveSettings} className="bg-purple-700 hover:bg-purple-800 text-white font-bold uppercase tracking-wide rounded-sm">
                Salva Impostazioni
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Confirmation Dialog */}
      <Dialog open={!!confirmDialog} onOpenChange={() => setConfirmDialog(null)}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase">
              {confirmDialog?.type === "block" && "Blocca Utente"}
              {confirmDialog?.type === "unblock" && "Sblocca Utente"}
              {confirmDialog?.type === "deleteUser" && "Elimina Utente"}
              {confirmDialog?.type === "deleteTournament" && "Elimina Torneo"}
            </DialogTitle>
            <DialogDescription>
              {confirmDialog?.type === "block" && `Sei sicuro di voler bloccare "${confirmDialog.name}"? L'utente non potra' piu' accedere alla piattaforma.`}
              {confirmDialog?.type === "unblock" && `Vuoi sbloccare "${confirmDialog.name}"?`}
              {confirmDialog?.type === "deleteUser" && `Sei sicuro di voler eliminare "${confirmDialog.name}"? Tutti i suoi tornei, giocatori e match verranno eliminati permanentemente.`}
              {confirmDialog?.type === "deleteTournament" && `Sei sicuro di voler eliminare il torneo "${confirmDialog.name}"?`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-sm" onClick={() => setConfirmDialog(null)}>Annulla</Button>
            <Button
              data-testid="admin-confirm-action"
              className={`rounded-sm font-bold uppercase ${confirmDialog?.type === "unblock" ? "bg-green-700 hover:bg-green-800" : "bg-red-600 hover:bg-red-700"} text-white`}
              onClick={() => {
                if (confirmDialog.type === "block") toggleBlock(confirmDialog.uid, true);
                else if (confirmDialog.type === "unblock") toggleBlock(confirmDialog.uid, false);
                else if (confirmDialog.type === "deleteUser") deleteUser(confirmDialog.uid);
                else if (confirmDialog.type === "deleteTournament") deleteTournament(confirmDialog.tid);
              }}
            >
              Conferma
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
